import { FunctionComponent, useEffect, useState } from 'react';
import {
  Button,
  Card,
  ChoiceList,
  Filters,
  FiltersProps,
  ResourceItem,
  ResourceList,
  ResourceListProps,
} from '@shopify/polaris';
import styled from 'styled-components';
import { FilterType } from '../container';

export interface ResourceModalContentProps
  extends Omit<ResourceListProps, 'selectedItems' | 'renderItem'> {
  filters?: FilterType;
  selectedItems: string[];
  showHeader?: boolean;
  renderItem?: ResourceListProps['renderItem'];
}

const StyledFilterControl = styled.div<{
  hideFiltersButton?: boolean;
}>`
  .Polaris-Filters-ConnectedFilterControl__MoreFiltersButtonContainer {
    display: ${(props) => (props.hideFiltersButton ? 'none' : undefined)};
  }
`;

const SelectButtonContainer = styled.div`
  margin-left: 1.25rem;
`;

const UnselectButton = styled.div`
  font-weight: 500;
  display: none;
  margin-right: 0.25rem;
  margin-left: 1.25rem;
  padding: 0.8rem;
`;

const ResourceListContainer = styled.div`
  .Polaris-ResourceList__FiltersWrapper + .Polaris-ResourceList,
  .Polaris-ResourceItem__ListItem {
    border-top: none;
  }

  .Polaris-ResourceList__FiltersWrapper {
    padding: 1.6rem 0;
  }

  .Polaris-TextField__Backdrop {
    background-color: #f1f2f3 !important;
    border: none;
  }

  .Polaris-ResourceItem__Container {
    padding: 0.1rem 0.2rem;
  }

  .Polaris-ResourceItem {
    background-color: transparent;

    &:hover {
      background-color: transparent;
    }

    .Polaris-Card {
      &:hover {
        background-color: rgba(246, 246, 247, 1);
      }
    }
  }

  .Polaris-ResourceItem--selected ${UnselectButton} {
    display: block;
  }

  .Polaris-ResourceItem--selected ${SelectButtonContainer} {
    display: none;
  }
`;

const ResourceItemContainer = styled.div`
  margin-bottom: 0.7rem;

  .Polaris-Card {
    padding: 1.2rem 2rem;
  }
`;

const ResourceItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterControlChildrenContainer = styled.div<{
  hasChildren: boolean;
}>`
  padding-left: ${(props) => (props.hasChildren ? '0.8rem' : undefined)};
`;

const FilterControl: FunctionComponent<FilterType> = (props) => {
  const {
    children,
    choices,
    hideFiltersButton,
    label,
    onChangeFilter,
    onQueryClear,
    selected,
    selectedFilterTags,
  } = props;

  const key = label ? label.toLowerCase() : '';
  const capitalizedSelectedFilterTags = selectedFilterTags?.map(
    (selectedFilterTag) => {
      let label = selectedFilterTag.label.toLowerCase();
      label = label.charAt(0).toUpperCase() + label.slice(1);
      return {
        ...selectedFilterTag,
        label,
      };
    },
  );

  const filters: FiltersProps['filters'] = [
    {
      filter: (
        <ChoiceList
          title={label}
          titleHidden
          choices={choices ?? []}
          selected={selected ?? []}
          onChange={onChangeFilter}
          allowMultiple
        />
      ),
      hideClearButton: true,
      key: key,
      label: label ?? '',
      shortcut: true,
    },
  ];

  return (
    <StyledFilterControl hideFiltersButton={hideFiltersButton}>
      <Filters
        {...props}
        onClearAll={onQueryClear}
        filters={filters}
        appliedFilters={capitalizedSelectedFilterTags}
      >
        <FilterControlChildrenContainer hasChildren={Boolean(children)}>
          {children}
        </FilterControlChildrenContainer>
      </Filters>
    </StyledFilterControl>
  );
};

const ResourceModalContent: FunctionComponent<ResourceModalContentProps> = (
  props,
) => {
  const { filters, onSelectionChange, renderItem, selectedItems, showHeader } =
    props;
  const [selected, setSelected] = useState(selectedItems);

  useEffect(() => {
    setSelected(selectedItems as string[]);
  }, [selectedItems]);

  const handleOnSelectionChange = (selected: string[]) => {
    setSelected(selected);
    onSelectionChange && onSelectionChange(selected);
  };

  const handleSelectSingleItem = (id: string) => {
    let updatedSelected = [...selected];

    if (updatedSelected.includes(id)) {
      updatedSelected = selected.filter((itemId) => itemId !== id);
    } else {
      updatedSelected = [...updatedSelected, id];
    }

    handleOnSelectionChange(updatedSelected);
  };

  const handleRenderItem = (item: any, id: string, index: number) => {
    return (
      <ResourceItem id={id} key={id} onClick={() => handleSelectSingleItem(id)}>
        <ResourceItemContainer>
          <Card>
            <ResourceItemContent>
              {renderItem && renderItem(item, id, index)}
              <SelectButtonContainer>
                <Button>Add</Button>
              </SelectButtonContainer>
              <UnselectButton>Remove</UnselectButton>
            </ResourceItemContent>
          </Card>
        </ResourceItemContainer>
      </ResourceItem>
    );
  };

  return (
    <ResourceListContainer>
      <ResourceList
        {...props}
        renderItem={handleRenderItem}
        selectedItems={selected}
        selectable={false}
        onSelectionChange={handleOnSelectionChange}
        filterControl={filters && <FilterControl {...filters} />}
        showHeader={showHeader}
      />
    </ResourceListContainer>
  );
};

export default ResourceModalContent;
